import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Headset,
  ArrowLeft,
  ArrowRight,
  PaperPlaneRight,
  WhatsappLogo,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import { api } from "@/lib/gado";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PAGE_SIZE = 10;

const TICKET_STATUS = {
  aberto: { label: "Aberto", cls: "border-gado-gold/40 bg-gado-gold/10 text-gado-gold" },
  em_atendimento: { label: "Em atendimento", cls: "border-blue-500/30 bg-blue-500/10 text-blue-600" },
  resolvido: { label: "Resolvido", cls: "border-gado-green/30 bg-gado-green/10 text-gado-green" },
};

const fmtDT = (d) =>
  d ? new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

export default function AdminSupport() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("todos");
  const [page, setPage] = useState(1);
  const [openTicket, setOpenTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [contactLoaded, setContactLoaded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-support", status, page],
    queryFn: async () =>
      (await api.get("/admin/support/tickets", {
        params: { status: status === "todos" ? undefined : status, page, page_size: PAGE_SIZE },
      })).data,
  });

  useQuery({
    queryKey: ["support-contact"],
    queryFn: async () => {
      const d = (await api.get("/support/contact")).data;
      if (!contactLoaded) {
        setWhatsapp(d.whatsapp || "");
        setEmail(d.email || "");
        setContactLoaded(true);
      }
      return d;
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-support"] });

  const saveContact = async () => {
    setSaving(true);
    try {
      await api.put("/admin/support/contact", { whatsapp, email });
      toast.success("Contato de suporte atualizado");
      qc.invalidateQueries({ queryKey: ["support-contact"] });
    } catch {
      toast.error("Falha ao salvar contato");
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!openTicket || !reply.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/support/tickets/${openTicket.ticket_id}/reply`, { message: reply.trim() });
      toast.success("Resposta enviada");
      setReply("");
      setOpenTicket(null);
      refresh();
    } catch {
      toast.error("Falha ao responder");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (ticket, newStatus) => {
    try {
      await api.put(`/admin/support/tickets/${ticket.ticket_id}/status`, { status: newStatus });
      toast.success(`Chamado marcado como ${TICKET_STATUS[newStatus].label.toLowerCase()}`);
      setOpenTicket(null);
      refresh();
    } catch {
      toast.error("Falha ao mudar status");
    }
  };

  const items = data?.items || [];
  const counts = data?.counts || {};
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const FILTERS = [
    ["todos", `Todos (${total})`],
    ["aberto", `Abertos (${counts.aberto || 0})`],
    ["em_atendimento", `Em atendimento (${counts.em_atendimento || 0})`],
    ["resolvido", `Resolvidos (${counts.resolvido || 0})`],
  ];

  return (
    <div className="space-y-5" data-testid="admin-support-page">
      <div>
        <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Suporte</h1>
        <p className="text-gado-muted text-sm">Chamados dos usuários e contato direto exibido na tela de Ajuda.</p>
      </div>

      {/* Contato direto (visível na tela Ajuda do usuário) */}
      <div className="rounded-xl border border-gado-line bg-white p-4" data-testid="support-contact-card">
        <h3 className="font-cabinet font-bold text-gado-ink text-sm mb-1">Contato direto</h3>
        <p className="text-xs text-gado-muted mb-3">Exibido como botões de WhatsApp e e-mail na tela de Ajuda do app.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="relative">
            <WhatsappLogo size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gado-muted" />
            <Input
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="WhatsApp (ex.: 5565999999999)"
              className="pl-9 bg-white"
              data-testid="support-whatsapp-input"
            />
          </div>
          <div className="relative">
            <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gado-muted" />
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-mail de suporte"
              className="pl-9 bg-white"
              data-testid="support-email-input"
            />
          </div>
        </div>
        <Button onClick={saveContact} disabled={saving} data-testid="support-contact-save"
          className="mt-3 bg-gado-green hover:bg-gado-greenDark text-white">
          {saving ? "Salvando…" : "Salvar contato"}
        </Button>
      </div>

      {/* Filtros de status */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([value, label]) => (
          <button
            key={value}
            onClick={() => { setStatus(value); setPage(1); }}
            data-testid={`support-filter-${value}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
              status === value
                ? "bg-gado-green text-white border-gado-green"
                : "bg-white text-gado-muted border-gado-line hover:border-gado-green/40"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-gado-line/40 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-gado-line bg-white p-10 text-center" data-testid="support-empty">
          <Headset size={32} className="mx-auto text-gado-muted mb-2" />
          <p className="text-sm text-gado-muted">Nenhum chamado por aqui.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((t) => (
            <button
              key={t.ticket_id}
              onClick={() => { setOpenTicket(t); setReply(""); }}
              data-testid={`support-ticket-${t.ticket_id}`}
              className="w-full text-left rounded-xl border border-gado-line bg-white p-4 hover:border-gado-green/40 hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-gado-ink text-sm truncate">{t.subject}</p>
                  <p className="text-xs text-gado-muted truncate">
                    {t.user_name || t.user_email} · atualizado {fmtDT(t.updated_at)}
                  </p>
                </div>
                <Badge variant="outline" className={`shrink-0 ${TICKET_STATUS[t.status]?.cls || ""}`}
                  data-testid={`support-status-${t.ticket_id}`}>
                  {TICKET_STATUS[t.status]?.label || t.status}
                </Badge>
              </div>
              <p className="mt-1.5 text-xs text-gado-muted line-clamp-1">
                {t.messages?.[t.messages.length - 1]?.message}
              </p>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-gado-muted" data-testid="support-total">{total} chamado(s)</p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="support-prev-page" aria-label="Página anterior">
            <ArrowLeft size={14} />
          </Button>
          <span className="text-xs text-gado-muted tabular-nums">{page}/{totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="support-next-page" aria-label="Próxima página">
            <ArrowRight size={14} />
          </Button>
        </div>
      </div>

      {/* Dialog: detalhe do chamado */}
      <Dialog open={!!openTicket} onOpenChange={(open) => !open && setOpenTicket(null)}>
        <DialogContent data-testid="support-ticket-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-cabinet">{openTicket?.subject}</DialogTitle>
            <DialogDescription>
              {openTicket?.user_name || openTicket?.user_email} · aberto em {fmtDT(openTicket?.created_at)}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-64 overflow-y-auto space-y-2 pr-1" data-testid="support-thread">
            {(openTicket?.messages || []).map((m, i) => (
              <div
                key={i}
                className={`rounded-lg p-3 text-sm max-w-[85%] ${
                  m.sender === "admin"
                    ? "ml-auto bg-gado-green text-white"
                    : "bg-gado-bg text-gado-ink"
                }`}
              >
                <p className="whitespace-pre-wrap">{m.message}</p>
                <p className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-white/60" : "text-gado-muted"}`}>
                  {m.sender === "admin" ? "Suporte" : "Usuário"} · {fmtDT(m.created_at)}
                </p>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Responder ao usuário…"
              rows={3}
              data-testid="support-reply-input"
            />
            <div className="flex gap-2">
              <Button onClick={sendReply} disabled={saving || !reply.trim()} data-testid="support-reply-send"
                className="flex-1 bg-gado-green hover:bg-gado-greenDark text-white">
                <PaperPlaneRight size={15} className="mr-1.5" /> {saving ? "Enviando…" : "Responder"}
              </Button>
              <Select
                value={openTicket?.status}
                onValueChange={(v) => changeStatus(openTicket, v)}
              >
                <SelectTrigger className="w-40" data-testid="support-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aberto">Aberto</SelectItem>
                  <SelectItem value="em_atendimento">Em atendimento</SelectItem>
                  <SelectItem value="resolvido">Resolvido</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
